import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Modal from '@common/components/Modal/Modal';
import { PdfIcon, ExcelIcon } from '@common/components/icons';
import * as styles from './SaveScheduledReportModal.module.css';

/**
 * SaveScheduledReportModal Component
 *
 * Modal for saving or editing a scheduled report with name, description, and format.
 * Displays inline error messages below the name field and auto-clears
 * errors when the user starts typing.
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onSave - Callback when save is clicked with { name, description, outputFormat }
 * @param {boolean} saving - Whether save is in progress
 * @param {string} error - Error message to display (e.g., duplicate name error)
 * @param {string} defaultName - Default name for the report
 * @param {string} defaultDescription - Default description for the report
 * @param {string} defaultOutputFormat - Default output format (pdf or excel)
 * @param {boolean} isEditMode - Whether this is edit mode (changes title and button text)
 */
export default function SaveScheduledReportModal({
  isOpen,
  onClose,
  onSave,
  saving = false,
  error = null,
  defaultName = '',
  defaultDescription = '',
  defaultOutputFormat = 'pdf',
  isEditMode = false,
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(defaultName);
  const [description, setDescription] = useState(defaultDescription);
  const [outputFormat, setOutputFormat] = useState(defaultOutputFormat);
  const [errorDismissed, setErrorDismissed] = useState(false);
  const nameInputRef = useRef(null);

  // Derived error state: show error prop unless user dismissed it by typing
  const visibleError = error && !errorDismissed ? error : null;

  // Reset errorDismissed when error prop changes (new error from parent)
  useEffect(() => {
    setErrorDismissed(false);
    if (error) {
      nameInputRef.current?.focus();
    }
  }, [error]);

  // Reset form when modal opens with new defaults
  useEffect(() => {
    if (isOpen) {
      setName(defaultName);
      setDescription(defaultDescription);
      setOutputFormat(defaultOutputFormat);
    }
  }, [isOpen, defaultName, defaultDescription, defaultOutputFormat]);

  // Clear error when user types in name field
  const handleNameChange = useCallback((e) => {
    setName(e.target.value);
    setErrorDismissed(true);
  }, []);

  const handleSave = useCallback(() => {
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      description: description.trim(),
      outputFormat,
    });
  }, [name, description, outputFormat, onSave]);

  const handleClose = useCallback(() => {
    setName(defaultName);
    setDescription('');
    setOutputFormat(defaultOutputFormat);
    onClose();
  }, [defaultName, defaultOutputFormat, onClose]);

  const isValid = name.trim().length > 0;

  const modalBody = (
    <div className={styles.form}>
      <div className={styles.field}>
        <label htmlFor="scheduled-report-name" className={styles.label}>
          {t('scheduledReports.modal.nameLabel')}
          <span className={styles.required}>*</span>
        </label>
        <input
          ref={nameInputRef}
          id="scheduled-report-name"
          type="text"
          className={`${styles.input} ${visibleError ? styles.inputError : ''}`}
          value={name}
          onChange={handleNameChange}
          placeholder={t('scheduledReports.modal.namePlaceholder')}
          disabled={saving}
          aria-invalid={!!visibleError}
          aria-describedby={visibleError ? 'scheduled-report-name-error' : undefined}
        />
        {visibleError && (
          <div
            id="scheduled-report-name-error"
            className={styles.fieldError}
            role="alert"
          >
            {visibleError}
          </div>
        )}
      </div>
      <div className={styles.field}>
        <label htmlFor="report-description" className={styles.label}>
          {t('common.description')}
        </label>
        <textarea
          id="report-description"
          className={styles.textarea}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('scheduledReports.modal.descriptionPlaceholder')}
          disabled={saving}
          rows={4}
        />
      </div>
      <div className={styles.field}>
        <span id="output-format-label" className={styles.label}>
          {t('scheduledReports.modal.formatLabel')}
        </span>
        <div className={styles.formatOptions} role="radiogroup" aria-labelledby="output-format-label">
          <label htmlFor="format-pdf" className={`${styles.formatOption} ${outputFormat === 'pdf' ? styles.selected : ''}`}>
            <input
              id="format-pdf"
              type="radio"
              name="outputFormat"
              value="pdf"
              checked={outputFormat === 'pdf'}
              onChange={() => setOutputFormat('pdf')}
              disabled={saving}
            />
            <PdfIcon size={20} className={styles.formatIcon} />
            <span className={styles.formatLabel}>PDF</span>
          </label>
          <label htmlFor="format-excel" className={`${styles.formatOption} ${outputFormat === 'excel' ? styles.selected : ''}`}>
            <input
              id="format-excel"
              type="radio"
              name="outputFormat"
              value="excel"
              checked={outputFormat === 'excel'}
              onChange={() => setOutputFormat('excel')}
              disabled={saving}
            />
            <ExcelIcon size={20} className={styles.formatIcon} />
            <span className={styles.formatLabel}>Excel</span>
          </label>
        </div>
      </div>
    </div>
  );

  const modalTitle = isEditMode 
    ? t('scheduledReports.modal.editTitle') 
    : t('scheduledReports.modal.title');
  
  const getSubmitText = () => {
    if (saving) return t('common.saving');
    if (isEditMode) return t('scheduledReports.modal.saveChanges');
    return t('common.save');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={modalTitle}
      body={modalBody}
      submitText={getSubmitText()}
      submitAction={handleSave}
      submitDisabled={!isValid || saving}
    />
  );
}

SaveScheduledReportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  error: PropTypes.string,
  defaultName: PropTypes.string,
  defaultDescription: PropTypes.string,
  defaultOutputFormat: PropTypes.oneOf(['pdf', 'excel']),
  isEditMode: PropTypes.bool,
};

SaveScheduledReportModal.defaultProps = {
  saving: false,
  error: null,
  defaultName: '',
  defaultDescription: '',
  defaultOutputFormat: 'pdf',
  isEditMode: false,
};

